import { useMemo } from "react"
import {
  Block,
  BlockProps,
  LabelMedium,
  ListItem,
  ListItemLabel,
  Skeleton,
} from "@mezo-org/mezo-clay"

import { useModalCustomOptions } from "#/shared/hooks"
import { useEstimateBridgeAssetFees } from "#/shared/hooks/bridge"
import { BridgeableTokens } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  bridgeUtils,
  separateThousandsWithComma,
} from "#/shared/utils"

type BridgeSummaryListProps = {
  amount: bigint | undefined
  token: BridgeableTokens
} & BlockProps

export default function BridgeSummaryList({
  amount,
  token,
  ...props
}: BridgeSummaryListProps) {
  const { depositId } = useModalCustomOptions<{ depositId: number }>()

  const isERC20Token = useMemo(() => token !== "BTC", [token])

  const {
    estimatedTransactionFee,
    estimatedTransactionFeeInUSD,
    serviceFee,
    isFetching: isFetchingBridgeTransactionFee,
  } = useEstimateBridgeAssetFees(token, amount, depositId)

  const isFetchingTransactionFees = isFetchingBridgeTransactionFee

  const summary: {
    label: string
    endEnhancer: string
    isFetching?: boolean
  }[] = [
    {
      label: "Transfer Time",
      endEnhancer: bridgeUtils.getEstimatedBridgeTime(
        !isERC20Token && !depositId,
      ),
    },
    {
      label: "Via",
      endEnhancer: "Native Bridge",
    },
    {
      label: "Network fees",
      endEnhancer: isERC20Token
        ? `${bigIntToHumanReadableFormat(
            estimatedTransactionFee,
            18,
            7,
          )} ETH ($${separateThousandsWithComma(estimatedTransactionFeeInUSD)})`
        : "Check wallet for bitcoin transaction fee",
      isFetching: isERC20Token ? isFetchingTransactionFees : false,
    },
    {
      label: "Service fees",
      endEnhancer: `${bigIntToHumanReadableFormat(serviceFee, 18, 5)} ${
        isERC20Token ? "ETH" : "BTC"
      }`,
      isFetching: isFetchingBridgeTransactionFee,
    },
  ]

  return (
    <Block as="ul" {...props}>
      {summary.map(({ label, endEnhancer, isFetching }) => (
        <ListItem
          key={label}
          endEnhancer={() =>
            isFetching ? (
              <Skeleton animation width="100px" height="1em" />
            ) : (
              <LabelMedium>{endEnhancer}</LabelMedium>
            )
          }
          overrides={{
            Content: {
              style: { borderBottom: "none", minHeight: "44px" },
            },
          }}
        >
          <ListItemLabel
            overrides={{
              LabelContent: {
                style: ({ $theme }) => ({
                  color: $theme.colors.contentPrimary,
                }),
              },
            }}
          >
            {label}
          </ListItemLabel>
        </ListItem>
      ))}
    </Block>
  )
}
